import React, { useState } from "react";
import { NavItemDataDataModel, NavItemModel } from "@models/layout";
import { AnimatePresence, motion } from "framer-motion";
import { FiChevronDown } from "react-icons/fi";
import { isEmpty } from "@utils/common";
import { twMerge } from "tailwind-merge";
import Link from "../Link";
import { FaChevronRight } from "react-icons/fa";
import useIcons from "@hooks/use-source-icons";

export interface NavItemPropsModel {
  key: string | number;
  navAttributes: NavItemModel;
  topOffset?: number;
  showBanner?: boolean;
}

const arrowVariants = {
  base: {
    rotate: 0,
  },
  hover: {
    rotate: 180,
  },
};

const blockedMenuVariants = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 10 },
};

const cta = {
  initial: {
    x: 0,
  },
  hover: {
    x: 10,
  },
};

const NavItem: React.FunctionComponent<NavItemPropsModel> = ({
  navAttributes,
  topOffset,
}) => {
  const { title, url } = navAttributes ?? {};
  const { children, type_of_menu: typeOfMenu } = navAttributes ?? {};
  const { data: childrenData } = children ?? {};
  const [showMenu, setShowMenu] = useState(false);
  const [blockedMenuId, setBlockedMenuId] = useState<number | null>(null);
  const additionalStyle = topOffset && topOffset > 0 ? { top: topOffset } : {};
  const { getFromReactIcons } = useIcons();

  const renderArrow = () => {
    if (childrenData?.length > 0) {
      return (
        <motion.span variants={arrowVariants} className="ml-1 mt-1">
          <FiChevronDown />
        </motion.span>
      );
    }
    return <></>;
  };

  const recurrsiveRendering = (
    dataObj: NavItemDataDataModel,
    depth: number,
    blockStyling: boolean = false
  ) => {
    if (!dataObj || isEmpty(dataObj)) return <></>;
    const { id, attributes } = dataObj;
    if (isEmpty(attributes)) return <></>;
    const {
      title,
      children,
      border_bottom: borderBottom,
      border_left: borderLeft,
      icon,
    } = attributes;
    const Icon = getFromReactIcons(icon);
    const borderLeftClassName = borderLeft ? "border-l border-grey-12" : "";
    const borderBottomClassName = borderBottom
      ? "border-b border-grey-12 mt-1"
      : "";

    let additionalStyles: string = "";
    if (blockStyling) {
      if (depth === 0) {
        additionalStyles = "text-grey-200 font-semibold";
      } else if (depth === 1) {
        additionalStyles = "text-grey-200";
      }
    }
    const dynamicClassName = twMerge(
      "min-w-[140px] text-sm",
      borderLeftClassName,
      borderBottomClassName
    );

    if (children?.data?.length > 0) {
      return (
        <div
          style={{ paddingLeft: `${18 * depth}px` }}
          key={id}
          className={dynamicClassName}
        >
          <div className={twMerge("py-2", additionalStyles)}>{title}</div>
          {children.data.map((child) => {
            return (
              <div key={child?.id}>
                {recurrsiveRendering(child, depth + 1, blockStyling)}
              </div>
            );
          })}
        </div>
      );
    }
    return (
      <div
        className={twMerge(
          " [&>div]:hover:rounded-md flex items-center",
          dynamicClassName
        )}
        style={{ paddingLeft: `${depth * 10}px` }}
        key={id}
      >
        <Link
          href={attributes.url}
          linkText={title}
          wrapperClassName="hover:rounded-md hover:bg-grey-10"
          className="text-grey-110 no-underline hover:font-normal py-3 px-2 block flex items-center"
          style={{
            fontSize: "inherit",
            color: "inherit",
          }}
        >
          {Icon && <Icon className="mr-3" />}
          <span>{title}</span>
        </Link>
      </div>
    );
  };

  const renderBlockedMenu = () => {
    const oneChildData =
      blockedMenuId !== null
        ? childrenData?.[blockedMenuId]
        : ({} as NavItemDataDataModel);

    const oneChildChildrenDataLengthIsGreaterThanNine =
      (oneChildData?.attributes?.children?.data?.length ?? 0) > 9;

    return (
      <motion.div
        className={`absolute top-[70px] bg-white shadow-md rounded-2xl overflow-hidden z-50 flex text-dark-purple-50`}
        variants={blockedMenuVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        style={additionalStyle}
      >
        <motion.div className="py-5 px-5 bg-grey-10">
          {childrenData.map((child, idx) => {
            const { id, attributes } = child;
            const { title } = attributes;
            return (
              <motion.div
                key={id}
                className="my-3 hover:bg-primary-blue-50 hover:text-white min-w-[140px] py-1.5 px-2 rounded-md"
                onHoverStart={() => {
                  setBlockedMenuId(idx);
                }}
              >
                {title}
              </motion.div>
            );
          })}
        </motion.div>
        <AnimatePresence>
          {blockedMenuId !== null && (
            <motion.div
              key={""}
              className={twMerge(
                "py-6 px-6",
                oneChildChildrenDataLengthIsGreaterThanNine
                  ? "grid grid-cols-2 gap-x-2 grid-rows-[repeat(8,auto)] grid-flow-col"
                  : ""
              )}
            >
              {oneChildData?.attributes?.children?.data?.map((child) => {
                return recurrsiveRendering(child, 0, true);
              })}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    );
  };

  const renderFlatMenu = () => {
    return (
      <motion.div
        className={`absolute top-[70px] !bg-white shadow-md rounded-2xl overflow-hidden z-50 flex text-dark-purple-50`}
        variants={blockedMenuVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        style={additionalStyle}
      >
        <motion.div className="py-3 px-5 bg-white">
          {childrenData.map((child) => {
            return recurrsiveRendering(child, 0, false);
          })}
        </motion.div>
      </motion.div>
    );
  };

  const renderChildren = () => {
    if (typeOfMenu === "flat") {
      return renderFlatMenu();
    } else if (typeOfMenu === "blocked") {
      return renderBlockedMenu();
    }
    return renderFlatMenu();
  };

  const defaultRendererForParentNav = () => (
    <motion.li
      className="flex items-center cursor-pointer h-full"
      whileHover="hover"
      animate="rest"
      initial="rest"
      variants={{}}
      onHoverStart={() => {
        setShowMenu(true);
      }}
      onHoverEnd={() => {
        setShowMenu(false);
        setBlockedMenuId(null);
      }}
    >
      {childrenData?.length ? (
        title
      ) : (
        <Link
          href={url}
          linkText={title}
          className="hover:font-normal"
          style={{
            textDecoration: "none",
            color: "inherit",
          }}
        />
      )}
      {renderArrow()}
      <AnimatePresence>
        {showMenu && childrenData?.length && renderChildren()}
      </AnimatePresence>
    </motion.li>
  );

  const rendererForCtaTypeParent = () => {
    return (
      <motion.li
        className="flex items-center cursor-pointer h-full text-primary-orange-50 font-medium"
        whileHover="hover"
        animate="rest"
        initial="rest"
        variants={{}}
      >
        <Link
          href={url}
          linkText={title}
          className="hover:font-normal"
          style={{
            textDecoration: "none",
            color: "inherit",
          }}
        />
        <motion.span variants={cta} className="ml-2">
          <FaChevronRight className="fill-primary-orange-50" />
        </motion.span>
      </motion.li>
    );
  };

  const renderParentNavigation = () => {
    let parentNav = <></>;
    switch (typeOfMenu) {
      case "cta":
        parentNav = rendererForCtaTypeParent();
        break;

      default:
        parentNav = defaultRendererForParentNav();
        break;
    }
    return parentNav;
  };

  return renderParentNavigation();
};
export default NavItem;
